import {Box, Stack, Typography} from '@mui/material';
import {InputField} from '@/pages/components/common';
import {AuditProgramSectionVO} from '@/services/auditService/auditProgramService';
import type {PafLetterPage} from '@/pages/components/audit-program/paf-section/constants';
import {PAF06_2_PAGE} from '@/pages/components/audit-program/paf-section/constants';
import ProgramSectionHeader from '@/pages/components/audit-program/a-section/ProgramSectionHeader';

export interface PafLetterViewProps {
    header: AuditProgramSectionVO;
    page?: PafLetterPage;
    refCode?: string;
    values: Record<string, string>;
    onChange: (key: string, value: string) => void;
}

/** PAF06.2 — specimen related-party request letter. */
export default function PafLetterView({
    header,
    page = PAF06_2_PAGE,
    refCode = 'PAF06.2',
    values,
    onChange,
}: PafLetterViewProps) {
    const meta = {
        title: page.title || PAF06_2_PAGE.title,
        greeting: page.greeting || PAF06_2_PAGE.greeting,
        subject: page.subject || PAF06_2_PAGE.subject,
        body: page.body?.length ? page.body : PAF06_2_PAGE.body,
        closing: page.closing || PAF06_2_PAGE.closing,
    };

    return (
        <Box sx={{p: 2, maxWidth: 720}}>
            <ProgramSectionHeader
                header={header}
                variant="a12"
                refCode={refCode}
                values={values}
                onChange={onChange}
            />
            <Typography
                variant="subtitle1"
                fontWeight={700}
                sx={{textAlign: 'center', letterSpacing: 0.3, mb: 2, mt: 0.5}}
            >
                {meta.title}
            </Typography>
            <Stack spacing={1.5}>
                <Box>
                    <Typography variant="body2" fontWeight={600} sx={{mb: 0.5}}>Date</Typography>
                    <InputField
                        type="text"
                        fullWidth
                        compact
                        value={values.letterDate ?? ''}
                        onChange={(e) => onChange('letterDate', e.target.value)}
                    />
                </Box>
                <Box>
                    <Typography variant="body2" fontWeight={600} sx={{mb: 0.5}}>
                        Name and address of client
                    </Typography>
                    <InputField
                        type="text"
                        fullWidth
                        compact
                        multiline
                        minRows={3}
                        value={values.addressee ?? ''}
                        onChange={(e) => onChange('addressee', e.target.value)}
                    />
                </Box>
                <Typography variant="body2">{meta.greeting}</Typography>
                <Typography variant="subtitle2" fontWeight={700}>{meta.subject}</Typography>
                {meta.body.map((para, i) => (
                    <Typography key={i} variant="body2" sx={{whiteSpace: 'pre-wrap'}}>
                        {para}
                    </Typography>
                ))}
                <Typography variant="body2">{meta.closing}</Typography>
                <Box>
                    <Typography variant="body2" fontWeight={600} sx={{mb: 0.5}}>
                        Contact / sign-off notes
                    </Typography>
                    <InputField
                        type="text"
                        fullWidth
                        compact
                        multiline
                        minRows={2}
                        value={values.signOff ?? ''}
                        onChange={(e) => onChange('signOff', e.target.value)}
                    />
                </Box>
            </Stack>
        </Box>
    );
}
